/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { Box, FormControl, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icons } from '../../../../../../Shared/Constants/Icons';
import { ScheduleDaysText, ScheduleDaysContainer } from '../../AllJobs.Style';
import { Options } from '../../../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { SelectInput } from '../../../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';

export interface Option {
  value: string;
  label: string;
}

interface IMonthlyScheduleProps {
  t: (key: string) => string;
  selectedJobData: any[];
  selectedDays: string[];
  handleDayClick: (day: string) => void;
  selectedWeek: string;
  setSelectedWeek: (val: string) => void;
  isWeekOpen: boolean;
  toggleWeekValue: () => void;
  WeekOptions: Option[];
  selectedDayOfWeek: string;
  setSelectedDayOfWeek: (val: string) => void;
  isDayOfWeekOpen: boolean;
  toggleDayOfWeekValue: () => void;
  DayOptions: Option[];
  daysStyle: any;
  Labels: any;
}

const MonthlySchedule: React.FC<IMonthlyScheduleProps> = ({
  t,
  selectedJobData,
  selectedDays,
  handleDayClick,
  selectedWeek,
  setSelectedWeek,
  isWeekOpen,
  toggleWeekValue,
  WeekOptions,
  selectedDayOfWeek,
  setSelectedDayOfWeek,
  isDayOfWeekOpen,
  toggleDayOfWeekValue,
  DayOptions,
  daysStyle,
  Labels,
}) => {
  const theme = useTheme();

  return (
    <>
      <ScheduleDaysText>{t('jobManagement:serviceDates')}</ScheduleDaysText>

      {/* Day-of-Month selection */}
      {selectedJobData[0]?.scheduleDays?.some((day: string) => day.startsWith('M-')) && (
        <ScheduleDaysContainer>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
            <Box
              key={date}
              onClick={() => handleDayClick(date.toString())}
              bgcolor={selectedDays.includes(date.toString()) ? theme.palette.secondary.dark : ''}
              fontWeight={
                selectedDays.includes(date.toString())
                  ? theme.typography.fontWeightBold
                  : theme.typography.fontWeightLight
              }
              sx={daysStyle}>
              {date}
            </Box>
          ))}
        </ScheduleDaysContainer>
      )}

      {/* Week/Day-of-Week selection */}
      {selectedJobData[0]?.scheduleDays?.some((day: string) => day.startsWith('W') || day.startsWith('L')) && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {/* Week Selector */}
          <FormControl fullWidth size="small">
            <Select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              input={<SelectInput />}
              IconComponent={() => (
                <img
                  src={Icons.DropdownIcon}
                  alt={t(Labels.TextDropDown)}
                  style={{
                    transform: isWeekOpen ? Labels.Rotate : 'none',
                    position: 'absolute',
                    right: '0.75rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    toggleWeekValue();
                  }}
                />
              )}
              open={isWeekOpen}
              onClose={toggleWeekValue}
              onOpen={toggleWeekValue}>
              {WeekOptions.map((week) => (
                <Options key={week.value} value={week.value}>
                  {week.label}
                </Options>
              ))}
            </Select>
          </FormControl>

          {/* Day-of-Week Selector */}
          <FormControl fullWidth size="small">
            <Select
              value={selectedDayOfWeek.toLowerCase?.() || ''}
              onChange={(e) => setSelectedDayOfWeek(e.target.value)}
              input={<SelectInput />}
              IconComponent={() => (
                <img
                  src={Icons.DropdownIcon}
                  alt={t(Labels.TextDropDown)}
                  style={{
                    transform: isDayOfWeekOpen ? Labels.Rotate : 'none',
                    position: 'absolute',
                    right: '0.75rem',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    toggleDayOfWeekValue();
                  }}
                />
              )}
              open={isDayOfWeekOpen}
              onClose={toggleDayOfWeekValue}
              onOpen={toggleDayOfWeekValue}>
              {DayOptions.map((day) => (
                <Options key={day.value} value={day.value}>
                  {day.label}
                </Options>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </>
  );
};

export default MonthlySchedule;
